import { useEffect, useRef, useState } from "react";
import { CalendarDays, Camera, Mail, MapPin, Phone, Save, User, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { US_STATES } from "../lib/kyc";

export default function ProfileDialog({ open, onClose }) {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [state, setState] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [saving, setSaving] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);

  useEffect(() => {
    if (!open || !user || user === false) return;
    const nameParts = splitFullLegalName(user.full_name || "");
    setUsername(cleanDisplayUsername(user.username || user.full_name || ""));
    setEmail(user.email || "");
    setFirstName(user.first_name || nameParts.first_name || "");
    setMiddleName(user.middle_name || nameParts.middle_name || "");
    setLastName(user.last_name || nameParts.last_name || "");
    setDateOfBirth(formatDobInput(user.date_of_birth || ""));
    setState(user.state || "");
    setProfileImage(user.profile_image_url || "");
  }, [open, user]);

  if (!open || !user || user === false) return null;

  const initial = (username || email || "S").charAt(0).toUpperCase();
  const displayPhone = formatProfilePhone(user.phone);

  const pickImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setImageBusy(true);
    try {
      const resized = await resizeImage(file);
      setProfileImage(resized);
      toast.success("Profile picture ready");
    } catch {
      toast.error("Could not load that image");
    } finally {
      setImageBusy(false);
    }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    const displayUsername = cleanDisplayUsername(username);
    if (displayUsername.length < 3) {
      toast.error("Display username must have at least 3 letters or numbers");
      return;
    }
    const cleanFirstName = cleanNamePart(firstName);
    const cleanMiddleName = cleanNamePart(middleName);
    const cleanLastName = cleanNamePart(lastName);
    if (!isValidNamePart(cleanFirstName) || !isValidNamePart(cleanLastName)) {
      toast.error("Enter your first and last name");
      return;
    }
    const dobIso = dobInputToIso(dateOfBirth);
    if (!dobIso) {
      toast.error("Date of birth is required");
      return;
    }
    const legalName = composeFullLegalName({ first_name: cleanFirstName, middle_name: cleanMiddleName, last_name: cleanLastName });
    if (!state) {
      toast.error("State is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        username: displayUsername,
        email,
        profile_image_url: profileImage,
        first_name: cleanFirstName,
        middle_name: cleanMiddleName,
        last_name: cleanLastName,
        full_name: legalName,
        date_of_birth: dobIso,
        state,
      });
      setUser(data);
      toast.success("Profile updated");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] grid items-start justify-items-center bg-slate-900/35 p-4 pt-12 backdrop-blur-sm sm:place-items-center sm:pt-4" onClick={onClose}>
      <form
        onSubmit={saveProfile}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[calc(100dvh-4rem)] w-full max-w-lg overflow-y-auto overscroll-contain rounded-2xl border border-[#2F6FA5]/20 bg-white p-6 pb-[calc(2rem+env(safe-area-inset-bottom))] text-slate-950 shadow-2xl sm:max-h-[calc(100dvh-2rem)]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#2F6FA5]">Profile</p>
            <h2 className="mt-1 font-display text-2xl font-black text-slate-950">My account</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 flex flex-col items-center gap-3">
          <div className="h-28 w-28 overflow-hidden rounded-full border-2 border-[#2F6FA5]/30 bg-[#EAF4FC] shadow-sm">
            {profileImage ? (
              <img src={profileImage} alt="Profile" className="h-full w-full object-cover" />
            ) : (
              <div className="grid h-full w-full place-items-center bg-gradient-to-br from-[#2F6FA5] to-[#173A5D] font-display text-4xl font-black text-black">
                {initial}
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={imageBusy}
              className="inline-flex items-center gap-2 rounded-full bg-[#2F6FA5] px-4 py-2 text-sm font-bold text-white hover:bg-[#4E91C8] disabled:opacity-60"
            >
              <Camera className="h-4 w-4" /> {imageBusy ? "Loading..." : "Edit profile picture"}
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" />
        </div>

        <div className="mt-6 space-y-4">
          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
              <User className="h-3.5 w-3.5" /> Display username
            </span>
            <input
              value={username}
              onChange={(e) => setUsername(cleanDisplayUsername(e.target.value))}
              minLength={2}
              maxLength={40}
              required
              placeholder="username"
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none transition-colors focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20"
            />
            <p className="mt-2 text-xs font-semibold text-slate-500">
              This is your public username. Full legal name stays separate for KYC.
            </p>
          </label>

          <div>
            <span className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
              <User className="h-3.5 w-3.5" /> Legal name
            </span>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                minLength={2}
                maxLength={50}
                required
                placeholder="First name"
                autoComplete="given-name"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none transition-colors focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
              <input
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                maxLength={50}
                placeholder="Middle name"
                autoComplete="additional-name"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none transition-colors focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                minLength={2}
                maxLength={50}
                required
                placeholder="Last name"
                autoComplete="family-name"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none transition-colors focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20 sm:col-span-2"
              />
            </div>
            <p className="mt-2 text-xs font-semibold text-slate-500">First and last name are required.</p>
          </div>

          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
              <Mail className="h-3.5 w-3.5" /> Gmail / email
            </span>
            <input
              type="email"
              value={email}
              readOnly
              required
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-500 outline-none"
            />
          </label>

          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
              <Phone className="h-3.5 w-3.5" /> Phone number
            </span>
            <input
              type="text"
              value={displayPhone}
              readOnly
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-500 outline-none"
            />
          </label>

          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
              <CalendarDays className="h-3.5 w-3.5" /> Date of birth
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(formatDobInput(e.target.value))}
              maxLength={10}
              readOnly={Boolean(user.date_of_birth)}
              disabled={Boolean(user.date_of_birth)}
              required
              placeholder="MM/DD/YYYY"
              className={`w-full rounded-xl border border-slate-200 px-4 py-3 outline-none transition-colors ${
                user.date_of_birth
                  ? "bg-slate-50 text-slate-500"
                  : "bg-white text-slate-950 focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20"
              }`}
            />
            {user.date_of_birth && (
              <p className="mt-2 text-xs font-semibold text-slate-500">Date of birth is locked.</p>
            )}
          </label>

          <label className="block">
            <span className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
              <MapPin className="h-3.5 w-3.5" /> State
            </span>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              required
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-950 outline-none transition-colors focus:border-[#2F6FA5] focus:ring-2 focus:ring-[#2F6FA5]/20"
            >
              <option value="">Select state</option>
              {US_STATES.map((item) => (
                <option key={item.code} value={item.code}>{item.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full border border-slate-200 px-5 py-3 font-bold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#2F6FA5] px-5 py-3 font-bold text-white hover:bg-[#4E91C8] disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
}

function formatProfilePhone(value) {
  if (!value) return "Not verified yet";
  const digits = String(value).replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length === 10) {
    return `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  }
  return String(value);
}

function cleanDisplayUsername(value) {
  return String(value || "").replace(/[^A-Za-z0-9]+/g, "").toLowerCase().slice(0, 40);
}

function cleanFullLegalName(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function cleanNamePart(value) {
  return cleanFullLegalName(value).replace(/[^\p{L}' -]/gu, "").trim();
}

function isValidNamePart(value) {
  return cleanNamePart(value).replace(/[^\p{L}]/gu, "").length >= 2;
}

function composeFullLegalName(value) {
  return [value.first_name, value.middle_name, value.last_name].map(cleanNamePart).filter(Boolean).join(" ");
}

function splitFullLegalName(value) {
  const parts = cleanFullLegalName(value).split(" ").filter(Boolean);
  if (parts.length <= 1) return { first_name: parts[0] || "", middle_name: "", last_name: "" };
  return {
    first_name: parts[0],
    middle_name: parts.slice(1, -1).join(" "),
    last_name: parts[parts.length - 1],
  };
}

function formatDobInput(value) {
  const raw = String(value || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function dobInputToIso(value) {
  const raw = String(value || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return validDob(iso[1], iso[2], iso[3]) ? raw : "";
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return "";
  const [, month, day, year] = match;
  const mm = month.padStart(2, "0");
  const dd = day.padStart(2, "0");
  return validDob(year, mm, dd) ? `${year}-${mm}-${dd}` : "";
}

function validDob(year, month, day) {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d) || y < 1900) return false;
  const parsed = new Date(y, m - 1, d);
  if (parsed.getFullYear() !== y || parsed.getMonth() !== m - 1 || parsed.getDate() !== d) return false;
  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return parsed <= todayDate;
}

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const image = new Image();
      image.onerror = reject;
      image.onload = () => {
        const maxSide = 512;
        const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.86));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
